import { useRef, useCallback, useState, type ReactNode } from 'react';
import { useForm } from '@tanstack/react-form';
import { Button } from '@/shared/ui/atoms';
import { Input } from '@/shared/ui/atoms';
import { EmailIcon, LockIcon, UserIcon, WarningIcon } from '@/shared/ui/atoms';
import { getFieldError } from '../../../../shared/lib';
import { useStepTransition } from '../../model/useStepTransition';
import type { Transition } from '../../model/useStepTransition';
import { SuccessStep } from '../SuccessStep';
import { stepContent, stepExiting, stepEntering } from '../AuthSceneLayout';
import { registerFormSchema, registerFormDefaults } from '../../model/registerForm.schema';
import * as s from './RegisterForm.css';

export interface UseRegisterFormOptions {
  loading?: boolean;
  error?: string;
  /**
   * 가입 요청 — 주소만 보낸다(87bac89b). 이름·비밀번호는 화면이 들고 있다가 인증코드와 함께 확인 요청으로
   * 보낸다. 서버는 메일함을 증명하기 전에는 어떤 입력도 저장하지 않는다.
   */
  onRegister?: (data: { email: string }) => void | Promise<void>;
  /**
   * 가입 확인 — 메일로 받은 6자리 인증코드와 처음에 적은 이름·비밀번호. 확인을 마치면 인사 담당·부서 팀장이
   * 부서에 넣어야 로그인된다.
   */
  onVerify?: (data: {
    email: string;
    code: string;
    username: string;
    password: string;
  }) => void | Promise<unknown>;
  /** 인증코드 다시 보내기 — 같은 주소로 가입 요청을 한 번 더 보낸다 */
  onResend?: (email: string) => void | Promise<void>;
  onLoginClick?: () => void;
  onRegisterComplete?: () => void;
}

export interface RegisterFormSceneProps {
  transition: Transition;
  frameGlitch: boolean;
  stepAnnouncement: string;
  sceneExiting: boolean;
  /** Panel className override for register layout */
  panelClassName: string;
  /** Content container className override */
  contentClassName: string;
  /** Form area className override */
  formAreaClassName: string;
}

export type RegisterStep = 'form' | 'verify' | 'success';

const SIGNUP_CODE_LENGTH = 6;

export interface UseRegisterFormReturn {
  /** Props to spread into AuthSceneLayout */
  sceneProps: RegisterFormSceneProps;
  /** Rendered form content (goes inside AuthSceneLayout children) */
  formContent: ReactNode;
}

/**
 * Hook that encapsulates registration form logic and UI.
 *
 * Returns `sceneProps` for AuthSceneLayout and `formContent` to render inside it.
 * Steps: form → success
 */
export function useRegisterForm({
  loading = false,
  error,
  onRegister,
  onVerify,
  onResend,
  onLoginClick,
  onRegisterComplete,
}: UseRegisterFormOptions): UseRegisterFormReturn {
  const formRef = useRef<HTMLFormElement>(null);
  const codeInputRef = useRef<HTMLInputElement>(null);

  // 가입 확인 단계 — 코드를 보낸 주소, 입력 중인 코드. 이름·비밀번호는 가입 요청에 싣지 않고 여기에 두었다가
  // 코드와 함께 확인 요청으로 보낸다(화면을 새로 고치면 처음부터 다시 적는다).
  const [registeredEmail, setRegisteredEmail] = useState('');
  const [code, setCode] = useState('');
  const [resendNotice, setResendNotice] = useState('');
  const pendingInputRef = useRef<{ username: string; password: string }>({
    username: '',
    password: '',
  });

  // Focus callback
  const onStepEntered = useCallback((nextStep: RegisterStep) => {
    if (nextStep === 'form') {
      formRef.current?.querySelector('input')?.focus();
    }
    if (nextStep === 'verify') {
      codeInputRef.current?.focus();
    }
  }, []);

  // Step transition
  const {
    step,
    transition,
    frameGlitch,
    isTransitioning,
    stepAnnouncement,
    triggerStepTransition,
    sceneExiting,
  } = useStepTransition<RegisterStep>({
    initialStep: 'form',
    successStep: 'success',
    onComplete: onRegisterComplete,
    onStepEntered,
  });

  // TanStack Form with Zod validation
  const form = useForm({
    defaultValues: registerFormDefaults,
    validators: {
      onSubmit: registerFormSchema,
      onChange: registerFormSchema,
    },
    onSubmit: async ({ value }) => {
      try {
        await onRegister?.({ email: value.email });
        // 가입이 접수되면 인증코드 단계로 간다 — 확인이 끝나야 등록 완료다(87bac89b)
        pendingInputRef.current = {
          username: value.username,
          password: value.password,
        };
        setRegisteredEmail(value.email);
        setCode('');
        setResendNotice('');
        triggerStepTransition('verify');
      } catch {
        // error handled externally via error prop
      }
    },
  });

  const handleVerifySubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (code.length !== SIGNUP_CODE_LENGTH) return;
    try {
      await onVerify?.({
        email: registeredEmail,
        code,
        username: pendingInputRef.current.username,
        password: pendingInputRef.current.password,
      });
      triggerStepTransition('success');
    } catch {
      // error handled externally via error prop
    }
  };

  const handleResend = async () => {
    try {
      await onResend?.(registeredEmail);
      setResendNotice(
        '인증코드를 다시 보냈습니다. 1분 안에 보낸 코드가 있으면 그 코드가 그대로 유효합니다.'
      );
    } catch {
      // error handled externally via error prop
    }
  };

  // --- Step className ---

  const getStepClassName = (t: Transition) => {
    if (t === 'exiting') return `${stepContent} ${stepExiting}`;
    if (t === 'entering') return `${stepContent} ${stepEntering}`;
    return stepContent;
  };

  // --- Step content ---

  const renderStepContent = (): ReactNode => {
    switch (step) {
      case 'form':
        return (
          <form
            ref={formRef}
            onSubmit={(e) => {
              e.preventDefault();
              e.stopPropagation();
              void form.handleSubmit();
            }}
            className={s.stepFormContent}
          >
            <form.Field name="email">
              {(field) => (
                <Input
                  label="이메일"
                  type="email"
                  autoComplete="email"
                  placeholder="email@example.com"
                  leftIcon={<EmailIcon />}
                  value={field.state.value}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    field.handleChange(e.target.value)
                  }
                  onBlur={field.handleBlur}
                  error={getFieldError(field.state.meta.errors, field.state.meta.isTouched)}
                  disabled={loading || isTransitioning}
                />
              )}
            </form.Field>

            <form.Field name="username">
              {(field) => (
                <Input
                  label="이름"
                  type="text"
                  autoComplete="name"
                  placeholder="홍길동"
                  leftIcon={<UserIcon />}
                  value={field.state.value}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    field.handleChange(e.target.value)
                  }
                  onBlur={field.handleBlur}
                  error={getFieldError(field.state.meta.errors, field.state.meta.isTouched)}
                  disabled={loading || isTransitioning}
                />
              )}
            </form.Field>

            <form.Field name="password">
              {(field) => (
                <Input
                  label="비밀번호"
                  type="password"
                  autoComplete="new-password"
                  placeholder="8자 이상, 대/소문자, 숫자, 특수문자"
                  leftIcon={<LockIcon />}
                  value={field.state.value}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    field.handleChange(e.target.value)
                  }
                  onBlur={field.handleBlur}
                  error={getFieldError(field.state.meta.errors, field.state.meta.isTouched)}
                  disabled={loading || isTransitioning}
                />
              )}
            </form.Field>

            <form.Field name="passwordConfirm">
              {(field) => (
                <Input
                  label="비밀번호 확인"
                  type="password"
                  autoComplete="new-password"
                  placeholder="비밀번호를 다시 입력해주세요"
                  leftIcon={<LockIcon />}
                  value={field.state.value}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    field.handleChange(e.target.value)
                  }
                  onBlur={field.handleBlur}
                  error={getFieldError(field.state.meta.errors, field.state.meta.isTouched)}
                  disabled={loading || isTransitioning}
                />
              )}
            </form.Field>

            {error && (
              <div className={s.errorBox} key={error} role="alert">
                <WarningIcon className={s.errorIcon} />
                <p className={s.errorText}>{error}</p>
              </div>
            )}

            <div className={s.buttonGroup}>
              <form.Subscribe selector={(state) => [state.isSubmitting]}>
                {([isSubmitting]) => (
                  <Button
                    type="submit"
                    variant="primary"
                    size="lg"
                    fullWidth
                    loading={loading || isSubmitting}
                    disabled={loading || isTransitioning}
                  >
                    등록하기
                  </Button>
                )}
              </form.Subscribe>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                fullWidth
                onClick={onLoginClick}
                disabled={isTransitioning}
              >
                이미 계정이 있으신가요? 로그인
              </Button>
            </div>
          </form>
        );

      case 'verify':
        return (
          <form
            onSubmit={(e) => {
              void handleVerifySubmit(e);
            }}
            className={s.stepFormContent}
          >
            <div>
              <span className={s.fieldLabel}>인증코드를 보낸 주소</span>
              <span className={s.verifyEmail}>{registeredEmail}</span>
            </div>
            <p className={s.verifyHint}>
              메일로 보낸 6자리 인증코드를 입력해 주세요. 인증코드를 넣어야 가입이 끝납니다. 코드는
              5분 뒤 만료되고, 이미 확인을 마쳤다면 코드는 다시 가지 않습니다.
            </p>
            <Input
              ref={codeInputRef}
              label="인증코드"
              type="text"
              placeholder="6자리 코드 입력"
              value={code}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setCode(e.target.value.replace(/\D/g, '').slice(0, SIGNUP_CODE_LENGTH))
              }
              inputMode="numeric"
              pattern="[0-9]*"
              autoComplete="one-time-code"
              maxLength={SIGNUP_CODE_LENGTH}
              leftIcon={<LockIcon />}
              disabled={loading || isTransitioning}
            />

            {error && (
              <div className={s.errorBox} key={error} role="alert">
                <WarningIcon className={s.errorIcon} />
                <p className={s.errorText}>{error}</p>
              </div>
            )}
            {resendNotice && (
              <p className={s.verifyHint} role="status">
                {resendNotice}
              </p>
            )}

            <div className={s.buttonGroup}>
              <Button
                type="submit"
                variant="primary"
                size="lg"
                fullWidth
                loading={loading}
                disabled={loading || isTransitioning || code.length !== SIGNUP_CODE_LENGTH}
              >
                확인
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                fullWidth
                onClick={() => {
                  void handleResend();
                }}
                disabled={loading || isTransitioning}
              >
                인증코드 다시 보내기
              </Button>
            </div>
          </form>
        );

      case 'success':
        return (
          <SuccessStep
            title="[가입 확인 완료]"
            message={
              // 확인 전 배정은 막혀 있어 확인을 마친 계정은 늘 부서 배정을 기다린다(87bac89b)
              <>
                가입 확인이 끝났습니다.
                <br />
                팀장이나 피플팀이 부서에 넣으면 로그인할 수 있습니다.
              </>
            }
          />
        );
    }
  };

  // --- Return ---

  const sceneProps: RegisterFormSceneProps = {
    transition,
    frameGlitch,
    stepAnnouncement,
    sceneExiting,
    panelClassName: s.registerPanel,
    contentClassName: s.registerContentContainer,
    formAreaClassName: s.registerFormArea,
  };

  const formContent: ReactNode = (
    <div className={getStepClassName(transition)}>{renderStepContent()}</div>
  );

  return { sceneProps, formContent };
}
